import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Flex, Text } from 'onefaceui-react-components';
import PropTypes from 'prop-types';
import routeDetails from 'react/api/actions/routeDetails';
import { getResourceTo, toastNotification } from 'react/utils/utils';
import {
  getPositionSourceNameForTrackCompany,
  getPositionSourceName
} from 'react/utils/coordinateUtils';

function ActualRouteLayer({ sourceScreen, context }) {
  const route = sourceScreen.model;
  const routeId = route.id;
  const dispatch = useDispatch();
  const { toolbarLayers, activeLayers } = useSelector((state) =>
    state.routeDetails.toJS()
  );
  const routeInfo = new Greenmile.RouteDetail.InfoRoute(
    sourceScreen.map,
    routeId
  );
  const layers = toolbarLayers[routeId] || [];
  const isSingleSource = layers.length && layers.length === 1;
  const isMultipleSource = layers.length > 1;

  const showWarningAlert = () => {
    toastNotification.addToast({
      id: `focus-equipment-alert-${routeId}`,
      type: 'warning',
      variant: 'secondary',
      data: {
        title: getResourceTo('alertgenericmodal.header.title.default'),
        description: getResourceTo('RouteDetail.equipmentFocus.empty')
      }
    });
  };

  const renderPath = (layer) => {
    const equipmentList = sourceScreen.equipments;
    const updatedLayers = {
      ...(activeLayers[routeId] || {}),
      [routeId]: [...(activeLayers[routeId] || []), layer]
    };

    Object.values(equipmentList).forEach((equipment) => {
      Greenmile.RouteDetail.Util.renderPathFromPositionSources(
        equipment,
        route,
        equipment.positionSources,
        updatedLayers[routeId]
      );
    });
  };

  const handleCheckAll = useCallback(
    (checked) => {
      if (!layers.length) {
        showWarningAlert();
        return;
      }

      if (checked) {
        routeInfo.enableCanMakeCoordinates();
        layers.forEach((layer) => renderPath(layer));
        dispatch(routeDetails.actionCreators.activateAllLayers(routeId));
        return;
      }

      routeInfo.blockCanMakeCoordinates();
      layers.forEach((layer) => routeInfo.removeLayerById(layer.id));
      dispatch(routeDetails.actionCreators.deactivateAllLayers(routeId));
    },
    [layers, routeInfo, renderPath, dispatch, routeId]
  );

  const handleCheckboxChange = useCallback(
    (checked, layer) => {
      if (checked) {
        routeInfo.enableCanMakeCoordinates();
        renderPath(layer);
        dispatch(routeDetails.actionCreators.activeLayer(routeId, layer));
        return;
      }

      routeInfo.removeLayerById(layer.id);
      routeInfo.blockCanMakeCoordinates();
      dispatch(routeDetails.actionCreators.inactiveLayer(routeId, layer.id));
    },
    [routeInfo, renderPath, dispatch, routeId]
  );

  const action = () => {
    const modal = new Greenmile.RouteDetail.MultiRoute();
    modal.openModal(route);

    const modalContainer = document.querySelector(
      `${modal.modalForm.selector} .popupFormContainer`
    );
    modalContainer.style.overflow = 'visible';
  };

  const routeColors = sourceScreen.routeColor;

  const checkboxColors = ['#FF41A6', '#FF7D00', ...routeColors];

  const isCheckedPrimaryLayer =
    layers.length > 0 &&
    layers.every((layer) =>
      (activeLayers[routeId] || []).some((l) => l.id === layer.id)
    );

  useEffect(() => {
    if (layers && !activeLayers[routeId]) {
      layers.forEach((layer) => {
        dispatch(routeDetails.actionCreators.activeLayer(routeId, layer));
      });
      context.setLayerStatusOnLocalStorageByTagName(true);
    }
  }, [toolbarLayers, routeId]);

  return (
    <Flex
      className="actual-route-layer"
      alignItems="start"
      flexDirection="column"
    >
      <Flex
        className="actual-route-layer"
        style={{ marginLeft: '5px' }}
        alignItems="start"
        flexDirection="column"
      >
        <Checkbox
          customCheckboxColor={checkboxColors.at(0)}
          checked={isCheckedPrimaryLayer}
          name="actualRoute"
          id={`actualRoute_${routeId}`}
          key={`actualRoute_${routeId}`}
          label="Actual Route"
          onChange={
            isSingleSource
              ? (checked) => handleCheckboxChange(checked, layers[0])
              : handleCheckAll
          }
        />
        <Flex className="content" flexDirection="column">
          {layers.length === 1 && layers[0].id !== 'UNKNOWN_SOURCE' && (
            <Text
              className="position-title"
              style={{ marginBottom: '4px' }}
              size="xs"
            >{`${getResourceTo('position.source.label')}: ${getPositionSourceName(layers[0])}`}</Text>
          )}
          <Flex
            shouldShow={isMultipleSource}
            className="layers"
            flexDirection="column"
            style={{ gap: '4px', marginBottom: '8px' }}
          >
            {layers.map((layer, index) => {
              const color = checkboxColors[index];
              const isChecked = !!(activeLayers[routeId] || []).find(
                ({ id }) => id === layer.id
              );

              return (
                <Checkbox
                  key={layer.id}
                  checked={isChecked}
                  name={`source-${layer.id}`}
                  id={`source-${layer.id}`}
                  label={
                    layer.sourceType === 'MOBILE'
                      ? getPositionSourceName(layer)
                      : getPositionSourceNameForTrackCompany(layer)
                  }
                  onChange={(checked) => handleCheckboxChange(checked, layer)}
                  customCheckboxColor={color}
                />
              );
            })}
          </Flex>
          <Button
            variant="secondary"
            data-testid="open-other-equipment-modal"
            onClick={action}
          >
            {getResourceTo('routeDetail.otherEquipment.button')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

ActualRouteLayer.propTypes = {
  sourceScreen: PropTypes.shape({
    map: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    equipments: PropTypes.object.isRequired,
    equipment: PropTypes.shape({
      idOfObject: PropTypes.number.isRequired
    }).isRequired,
    routeColor: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  context: PropTypes.shape({
    setLayerStatusOnLocalStorageByTagName: PropTypes.func.isRequired,
    getLayerStatusOnLocalStorage: PropTypes.func.isRequired
  }).isRequired
};

export default ActualRouteLayer;
